import { useState } from "react";
import { motion } from "framer-motion";
import { AnimatedSection, StaggerContainer, StaggerItem, Card3D } from "../../../components/animations/AnimatedElements";

const bizAreas = [
  {
    icon: "ri-search-eye-line",
    title: "현장 분석 · 컨설팅",
    items: ["고객 현장 직접 분석", "도입 전략 수립", "ROI 기반 기획"],
    desc: "ADX 플랫폼을 기반으로 고객의 현장 환경을 정밀 분석하고, 최적의 AI·디지털트윈 도입 전략을 컨설팅으로 제시합니다.",
  },
  {
    icon: "ri-brain-line",
    title: "AI Agent 솔루션",
    items: ["영상·데이터 AI 분석", "실시간 객체 인식", "이상 감지·자동 알림"],
    desc: "CCTV 영상부터 센서 데이터까지 AI Agent가 수집·분석하여 보안, 안전, 운영 효율화 솔루션을 구축합니다.",
  },
  {
    icon: "ri-cube-line",
    title: "Digital Twin 구현",
    items: ["3D 공간 디지털 복제", "Unreal/Unity 기반", "실시간 시뮬레이션"],
    desc: "Unreal·Unity 엔진으로 고객 현장을 3D 디지털트윈으로 구현하여 현실과 가상을 연결하는 스마트 관리 환경을 제공합니다.",
  },
  {
    icon: "ri-bar-chart-box-line",
    title: "데이터 인텔리전스",
    items: ["유동인구·행동 분석", "비즈니스 인사이트", "개인정보 보호 처리"],
    desc: "수집된 AI·디지털트윈 데이터를 비즈니스 인텔리전스로 전환하여 의사결정을 지원하고 새로운 비즈니스 가치를 창출합니다.",
  },
];

export default function VisionSection() {
  const [hovered, setHovered] = useState<number | null>(null);

  return (
    <section id="vision" className="py-28 bg-brand-dark relative overflow-hidden">
      {/* Background Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-cyan-500/3 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <AnimatedSection>
          <div className="section-header">
            <span className="section-accent" />
            <span className="section-label">Vision & Business</span>
          </div>

          {/* Vision Statement */}
          <div className="grid lg:grid-cols-2 gap-12 mb-20 items-center">
            <div>
              <h2 className="section-title mb-6">
                비전 · <span className="text-cyan-400">사업 영역</span>
              </h2>
              <div className="border-l-4 border-cyan-500 pl-6 mb-6">
                <p className="text-xl text-white font-semibold italic">"우리 생활을 창조하다"</p>
                <p className="text-gray-400 text-sm mt-1">CITY-AI 핵심 가치</p>
              </div>
              <p className="text-gray-400 leading-relaxed text-base mb-4">
                CITY-AI는 <strong className="text-white">ADX(AI & Digital Twin eXperience) 플랫폼</strong>을 기반으로
                컨설팅부터 솔루션 구축·운영까지 전 과정을 함께합니다. CCTV 영상 분석에서 출발해 지금은
                <strong className="text-white"> AI Agent, 디지털트윈, 데이터 인텔리전스</strong> 전 영역을 아우르는
                통합 AI 혁신 파트너로 성장했습니다.
              </p>
              <p className="text-gray-400 leading-relaxed text-base">
                글로벌 CCTV AI Agent 시장 점유율 확대를 넘어,
                <strong className="text-white"> 국내 우수조달 및 TOP 5 기업</strong>으로 성장하고
                ADX 플랫폼으로 글로벌 시장을 공략합니다.
              </p>
            </div>
            
            <motion.div 
              className="relative h-64 lg:h-80 rounded-lg overflow-hidden"
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.3 }}
            >
              <img
                src="https://readdy.ai/api/search-image?query=futuristic%20digital%20twin%203D%20city%20visualization%20glowing%20holographic%20map%20of%20Seoul%20subway%20stations%20with%20AI%20data%20streams%20neural%20network%20nodes%20smart%20city%20infrastructure%20real-time%20analytics%20panels%20deep%20dark%20background%20with%20cyan%20teal%20neon%20lights%20cinematic%20tech%20visualization&width=700&height=500&seq=vision001&orientation=landscape"
                alt="디지털트윈 비전"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/70 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                <div className="flex items-center justify-between text-xs text-gray-300">
                  <span className="flex items-center gap-1.5">
                    <motion.span
                      className="w-2 h-2 bg-cyan-400 rounded-full"
                      animate={{ scale: [1, 1.3, 1], opacity: [1, 0.7, 1] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    />
                    실시간 AI 분석 중
                  </span>
                  <span className="text-cyan-400">276개 역 공급 목표</span>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Business Areas - Card Grid */}
          <StaggerContainer className="grid md:grid-cols-2 lg:grid-cols-4 gap-4" staggerDelay={0.1}>
            {bizAreas.map((area, idx) => (
              <StaggerItem key={area.title}>
                <Card3D>
                  <div
                    className={`relative bg-brand-darkCard border rounded-lg p-6 cursor-pointer transition-all duration-300 overflow-hidden ${
                      hovered === idx ? "border-cyan-500/60 shadow-glow-sm" : "border-cyan-900/30 hover:border-cyan-900/60"
                    }`}
                    onMouseEnter={() => setHovered(idx)}
                    onMouseLeave={() => setHovered(null)}
                  >
                    {/* Hover Gradient Background */}
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-teal-500/5 pointer-events-none"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: hovered === idx ? 1 : 0 }}
                      transition={{ duration: 0.3 }}
                    />
                    
                    <div className="relative z-10">
                      <motion.div
                        className="icon-box mb-4"
                        whileHover={{ rotate: 5, scale: 1.1 }}
                        transition={{ type: "spring", stiffness: 400 }}
                      >
                        <i className={`${area.icon} text-cyan-400 text-xl`} />
                      </motion.div>
                      <h3 className="text-white font-semibold text-base mb-3">{area.title}</h3>
                      <p className="text-gray-500 text-xs leading-relaxed mb-4">{area.desc}</p>
                      <ul className="space-y-1.5">
                        {area.items.map((item) => (
                          <motion.li
                            key={item}
                            className="flex items-center gap-2 text-xs text-gray-400"
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.3 }}
                          >
                            <span className="w-1 h-1 bg-cyan-500 rounded-full flex-shrink-0" />
                            {item}
                          </motion.li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </Card3D>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </AnimatedSection>
      </div>
    </section>
  );
}
